import Dropzone from "dropzone";
import Cropper from "cropperjs";
import { Controller } from "stimulus";
import { DirectUpload } from "@rails/activestorage";
import {
  getMetaValue,
  toArray,
  findElement,
  removeElement,
  insertAfter
} from "../helpers";

// Connects to data-controller="dropzone"
export default class extends Controller {
  static targets = ["input"];

  connect() {
    this.cropQueue = [];
    this.isCropping = false;
    this.inputElement = this.inputTarget;
    this.directUploadUrl =
      this.inputElement?.dataset?.directUploadUrl ||
      this.inputElement?.getAttribute("data-direct-upload-url") ||
      this.element.dataset.directUploadUrl ||
      "/rails/active_storage/direct_uploads";
    this.inputName = this.inputElement?.name;
    this.dropZone = createDropZone(this);
    this.hideFileInput();
    this.bindEvents();
    Dropzone.autoDiscover = false; // necessary quirk for Dropzone error in console
  }

  // Private
  hideFileInput() {
    this.inputTarget.style.display = "none";
  }

  bindEvents() {
    this.dropZone.on("processing", (_file) => {
      this.toggleSubmitButton(false);
    });

    this.dropZone.on("addedfile", (file) => this.handleAddedFile(file));

    this.dropZone.on("removedfile", (file) => this.handleRemovedFile(file));

    this.dropZone.on("canceled", file => {
      file.controller && file.controller.xhr.abort();
    });

    this.dropZone.on("complete", (_file) => {
      this.toggleSubmitButton(true);
    });

    this.dropZone.uploadFiles = this.fakeUploadProgress.bind(this.dropZone);
  }

  handleAddedFile(file) {
    if (this.maxFiles === 1 && this.dropZone.files.length > 1) {
      // Dropzone won't enforce maxFiles for us (set to null), so keep only the latest file
      const [first] = this.dropZone.files;
      if (first && first !== file) {
        this.dropZone.removeFile(first);
      }
    }

    this.toggleSubmitButton(false);

    if (file.startUploadImmediately) {
      delete file.startUploadImmediately;
      this.startUpload(file);
      return;
    }

    if (this.shouldCrop && !file.skipCrop && this.isImageFile(file)) {
      this.enqueueForCropping(file);
    } else {
      this.startUpload(file);
    }
  }

  handleRemovedFile(file) {
    this.removeFromCropQueue(file);
    file.controller && removeElement(file.controller.hiddenInput);
  }

  enqueueForCropping(file) {
    this.cropQueue.push(file);
    this.processCropQueue();
  }

  removeFromCropQueue(file) {
    this.cropQueue = this.cropQueue.filter(queued => queued !== file);
  }

  async processCropQueue() {
    if (this.isCropping || this.cropQueue.length === 0) return;
    this.isCropping = true;

    const file = this.cropQueue.shift();
    try {
      const croppedFile = await this.openCropper(
        file,
        this.element.getAttribute("data-crop-image-aspect-ratio"),
        this.element.getAttribute("data-crop-image-width"),
        this.element.getAttribute("data-crop-image-height")
      );

      if (croppedFile) {
        croppedFile.skipCrop = true; // prevent re-cropping when added back
        croppedFile.startUploadImmediately = true;
        this.dropZone.removeFile(file);
        this.dropZone.addFile(croppedFile); // triggers addedfile -> startUpload immediately
      } else {
        this.dropZone.removeFile(file); // cancelled
      }
    } finally {
      this.isCropping = false;
      this.processCropQueue();
    }
  }

  async openCropper(file, aspectRatio = "1 / 1", width = 500, height = 500) {
    const imageSrc = await this.readFileAsDataURL(file);
    return new Promise((resolve) => {
      const cropModal = document.createElement("div");
      cropModal.className = "fixed inset-0 z-50 flex items-center justify-center bg-black/60";
      cropModal.innerHTML = `
        <div class="w-full max-w-lg overflow-hidden rounded-lg bg-white shadow-2xl">
          <div id="cropperImageContainer" class="max-h-[70vh] overflow-hidden bg-slate-100">
            <img id="cropperImage" class="block h-full w-full object-contain" src="${imageSrc}" alt="Cropper preview" />
          </div>
          <div class="flex items-center justify-end gap-3 border-t border-slate-200 bg-slate-50 p-4">
            <button id="crop-cancel-button" type="button" class="inline-flex items-center justify-center rounded border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100">Cancel</button>
            <button id="crop-save-button" type="button" class="inline-flex items-center justify-center rounded bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800">Crop</button>
          </div>
        </div>
      `;
      document.body.style.overflow = "hidden";
      document.body.appendChild(cropModal);

      const cropperImage = cropModal.querySelector("#cropperImage");
      const parsedAspect = this.parseAspectRatio(aspectRatio);
      const aspect = Number.isFinite(parsedAspect) ? parsedAspect : NaN; // NaN = freeform
      const containerRect = cropModal.querySelector("#cropperImageContainer").getBoundingClientRect();
      const cropper = new Cropper(cropperImage, {
        aspectRatio: aspect,
        viewMode: 1,
        rotatable: false,
        cropBoxMovable: false,
        cropBoxResizable: false,
        dragMode: "move",
        guides: false,
        responsive: true,
        minContainerWidth: containerRect.width,
        minContainerHeight: containerRect.height
      });

      const closeModal = () => {
        cropper && cropper.destroy();
        if (cropModal.parentNode) cropModal.parentNode.removeChild(cropModal);
        document.body.style.overflow = "";
      };

      cropModal.querySelector("#crop-cancel-button").addEventListener("click", () => {
        closeModal();
        resolve(null);
      });

      cropModal.querySelector("#crop-save-button").addEventListener("click", () => {
        cropper.getCroppedCanvas({ width: width, height: height }).toBlob((blob) => {
          const croppedFile = new File([blob], file.name, { type: "image/jpeg" });
          closeModal();
          resolve(croppedFile);
        }, "image/jpeg");
      });
    });
  }

  readFileAsDataURL(file) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(file);
    });
  }

  startUpload(file) {
    if (!file.accepted) file.accepted = true; // ensure Dropzone’s acceptance gate doesn’t block direct upload
    createDirectUploadController(this, file).start();
  }

  toggleSubmitButton(enable) {
    const submitButton = this.element.closest("form")?.querySelector('button[type="submit"]');
    if (submitButton) {
      submitButton.disabled = !enable;
    }
  }

  fakeUploadProgress(files) {
    files.forEach((file) => {
      const minSteps = 6;
      const maxSteps = 60;
      const timeBetweenSteps = 100;
      const bytesPerStep = 100000;
      const totalSteps = Math.round(Math.min(maxSteps, Math.max(minSteps, file.size / bytesPerStep)));

      for (let step = 0; step < totalSteps; step++) {
        const duration = timeBetweenSteps * (step + 1);
        setTimeout(() => {
          file.upload = {
            progress: 100 * (step + 1) / totalSteps,
            total: file.size,
            bytesSent: (step + 1) * file.size / totalSteps,
          };

          this.emit("uploadprogress", file, file.upload.progress, file.upload.bytesSent);
          if (file.upload.progress === 100) {
            file.status = Dropzone.SUCCESS;
            this.emit("success", file, "success", null);
            this.emit("complete", file);
            this.processQueue();
          }
        }, duration);
      }
    });
  }


  get headers() {
    return { "X-CSRF-Token": getMetaValue("csrf-token") };
  }

  get url() {
    return this.directUploadUrl || this.inputElement?.dataset?.directUploadUrl;
  }

  get maxFiles() {
    const value = this.data.get("maxFiles");
    return value ? Number(value) : 1;
  }

  get maxFileSize() {
    return this.data.get("maxFileSize") || 256;
  }

  get acceptedFiles() {
    return this.data.get("acceptedFiles");
  }

  get addRemoveLinks() {
    return this.data.get("addRemoveLinks") || true;
  }

  get shouldCrop() {
    return this.element.hasAttribute("data-crop-image");
  }

  parseAspectRatio(value) {
    if (!value || value.trim() === "") return NaN; // freeform
    return value.includes("/") ? Number(value.split("/")[0]) / Number(value.split("/")[1]) : parseFloat(value);
  }

  isImageFile(file) {
    if (file.type) return file.type.startsWith("image/");
    const name = file.name || "";
    return /\.(jpe?g|png|gif|webp|bmp|tiff?)$/i.test(name);
  }
}

class DirectUploadController {
  constructor(source, file) {
    this.url = source.url;
    this.source = source;
    this.file = file;
    this.directUpload = createDirectUpload(file, this.url, this);
  }

  start() {
    this.file.controller = this;
    this.hiddenInput = this.createHiddenInput();
    this.directUpload.create((error, attributes) => {
      if (error) {
        removeElement(this.hiddenInput);
        this.emitDropzoneError(error);
      } else {
        this.hiddenInput.value = attributes.signed_id;
        this.emitDropzoneSuccess();
      }
    });
  }

  createHiddenInput() {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = this.source.inputName || this.source.inputElement?.name;
    insertAfter(input, this.source.inputElement);
    return input;
  }

  directUploadWillStoreFileWithXHR(xhr) {
    this.bindProgressEvent(xhr);
    this.emitDropzoneUploading();
  }

  bindProgressEvent(xhr) {
    this.xhr = xhr;
    this.xhr.upload.addEventListener("progress", event =>
      this.uploadRequestDidProgress(event)
    );
  }

  uploadRequestDidProgress(event) {
    const element = this.source.element;
    const progress = (event.loaded / event.total) * 100;
    findElement(
      this.file.previewTemplate,
      ".dz-upload"
    ).style.width = `${progress}%`;
  }

  emitDropzoneUploading() {
    this.file.status = Dropzone.UPLOADING;
    this.source.dropZone.emit("processing", this.file);
  }

  emitDropzoneError(error) {
    this.file.status = Dropzone.ERROR;
    this.source.dropZone.emit("error", this.file, error);
    this.source.dropZone.emit("complete", this.file);
  }

  emitDropzoneSuccess() {
    this.file.status = Dropzone.SUCCESS;
    this.source.dropZone.emit("success", this.file);
    this.source.dropZone.emit("complete", this.file);
  }
}

function createDirectUploadController(source, file) {
  return new DirectUploadController(source, file);
}

function createDirectUpload(file, url, controller) {
  return new DirectUpload(file, url, controller);
}

function createDropZone(controller) {
  return new Dropzone(controller.element, {
    url: controller.url,
    headers: controller.headers,
    maxFiles: controller.maxFiles === 1 ? null : controller.maxFiles,
    maxFilesize: controller.maxFileSize,
    acceptedFiles: controller.acceptedFiles,
    addRemoveLinks: controller.addRemoveLinks,
    autoQueue: false
  });
}
